import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2/control/';

const Stat = ({ value, label }) => (
  <div>
    <div className="num text-[1.8rem] font-semibold leading-none">{value ?? '-'}</div>
    <div className="mt-1 text-sm text-[var(--ink-2)]">{label}</div>
  </div>
);

// Julia (Buddy): who answers each of our mailboxes (Aurélien, 2026-10-29).
const JULIA_MODES = [
  ['gorgias', 'Gorgias: Lia answers'],
  ['buddy_shadow', 'Buddy shadow: Julia writes notes, Lia answers'],
  ['buddy_live', 'Buddy live: Julia answers, Lia holds'],
];
const JuliaModes = ({ value, busy, save }) => {
  const [rows, setRows] = useState(Object.entries(value || {}));
  const [address, setAddress] = useState('');
  useEffect(() => { setRows(Object.entries(value || {})); }, [value]);
  const push = (next) => {
    const live = next.filter(([, m]) => m === 'buddy_live').map(([a]) => a).filter((a) => (value || {})[a] !== 'buddy_live');
    save({ julia_mode: Object.fromEntries(next) }, live.length
      ? `Julia will answer ${live.join(', ')} in Buddy, and Lia will hold every Gorgias reply there. Go live?` : null);
  };
  return (
    <div className="mt-4 text-sm">
      {rows.length === 0 && <p className="text-[var(--ink-3)]">Every mailbox is on Gorgias.</p>}
      {rows.map(([a, m]) => (
        <div key={a} className="mt-2 flex flex-wrap items-center gap-2">
          <span className="w-64 font-medium">{a}</span>
          <select value={m} disabled={busy} onChange={(e) => push(rows.map(([x, y]) => [x, x === a ? e.target.value : y]))}
            className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1">
            {JULIA_MODES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <button type="button" disabled={busy} onClick={() => push(rows.filter(([x]) => x !== a))} className="text-xs text-[var(--ink-3)] hover:underline">remove (back to Gorgias)</button>
        </div>))}
      <div className="mt-3 flex items-center gap-2">
        <input type="email" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="support@lamptwist.be" disabled={busy}
          className="w-64 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
        <button type="button" disabled={busy || !address.includes('@') || rows.some(([a]) => a === address.trim().toLowerCase())}
          onClick={() => { push([...rows, [address.trim().toLowerCase(), 'buddy_shadow']]); setAddress(''); }}
          className="rounded-md border border-[var(--line)] px-3 py-1 hover:bg-[var(--line-2)] disabled:opacity-40">Add in shadow</button>
      </div>
    </div>
  );
};

const ControlV2 = () => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { axiosInstance.get(API).then(({ data }) => setC(data)).catch((e) => setError(e?.response?.status === 403
    ? 'Control is only open to the support leads.' : 'Settings could not be loaded.')); }, []);

  const save = async (patch, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.patch(API, patch);
      setC(data);
    } catch {
      setError('Not saved. Try again.');
    }
    setBusy(false);
  };

  if (!c) return <V2Shell><p className="px-16 py-10 text-[var(--ink-2)]">{error || 'Loading'}</p></V2Shell>;
  const r = c.readiness;

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Control</h1>
        <p className="prose-body mt-3 text-[var(--ink-2)]">Replai drafts every ticket with the Bible agent: it reads the ticket, looks up live data and follows the Support Bible. It only ever posts an internal note; nothing reaches a customer unless someone presses "Send to customer" and confirms.</p>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

        <section className="mt-10">
          <h2 className="text-[1.2rem] font-semibold">Bible agent, last {r.days} days</h2>
          <div className="mt-4 grid grid-cols-2 gap-6 sm:grid-cols-4">
            <Stat value={r.shadow_runs} label="tickets tested" />
            <Stat value={r.completed} label="answered" />
            <Stat value={`${r.customer_replies - r.guard_failures}/${r.customer_replies}`} label="replies passed the safety checks" />
            <Stat value={r.mean_secs ? `${r.mean_secs}s` : '-'} label="per ticket" />
          </div>
          {r.judged > 0 && (
            <p className="prose-body mt-6">
              Compared with what the team actually sent on {r.judged} tickets: correct on <span className="num font-semibold">{r.v2_correct}</span>, and
              as good as or better than the current Replai on <span className="num font-semibold">{r.v2_better_or_equal}</span>.
            </p>
          )}
          <p className="prose-body mt-6">Live since the switch: {r.live_v2_runs} tickets drafted, {r.live_v2_posted} notes posted.</p>
        </section>

        <section className="mt-10 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
          <h2 className="text-[1.2rem] font-semibold">Gorgias views Replai reads</h2>
          <p className="mt-1 text-[0.95rem] text-[var(--ink-2)]">Replai drafts a note for every new ticket in these views. Add a view to cover more tickets; remove one to stop drafting there.</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {(c.views?.used || []).map((v) => (
              <li key={v.id} className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--paper)] py-1 pl-3 pr-1.5 text-sm">
                {v.name}
                <button type="button" disabled={busy || (c.views.used.length <= 1)} title="Stop reading this view"
                  onClick={() => save({ ingest_view_ids: c.views.used.filter((x) => x.id !== v.id).map((x) => x.id) },
                    `Stop drafting notes for tickets in "${v.name}"?`)}
                  className="rounded-full px-1.5 text-[var(--ink-3)] hover:bg-[var(--line-2)] hover:text-[var(--ink)] disabled:opacity-40">×</button>
              </li>
            ))}
          </ul>
          <label className="mt-4 flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={c.include_assigned_tickets} disabled={busy}
              onChange={(e) => save({ include_assigned_tickets: e.target.checked })} />
            <span>
              <span className="font-medium">Also tickets that are already assigned</span>
              <span className="block text-sm text-[var(--ink-2)]">Most of these views only show unassigned tickets. With this on, Replai also drafts a note for assigned open tickets with the same tags, each time the customer writes. It never changes who a ticket is assigned to.</span>
            </span>
          </label>
          {(c.views?.available || []).length > 0 && (
            <label className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-[var(--ink-2)]">Add a view:</span>
              <select disabled={busy} value="" className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5"
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const v = c.views.available.find((x) => x.id === id);
                  if (v) save({ ingest_view_ids: [...c.views.used.map((x) => x.id), id] }, `Start drafting notes for tickets in "${v.name}"?`);
                }}>
                <option value="">Choose a Gorgias view…</option>
                {c.views.available.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
          )}
        </section>

        <section className="mt-10 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
          <h2 className="text-[1.2rem] font-semibold">In Gorgias</h2>
          <label className="mt-4 flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={c.send_button_enabled} disabled={busy}
              onChange={(e) => save({ send_button_enabled: e.target.checked }, e.target.checked
                ? 'Show a "Send to customer" button on Ready-to-send notes? The reply goes out as the employee who confirms it.' : null)} />
            <span>
              <span className="font-medium">"Send to customer" button on notes</span>
              <span className="block text-sm text-[var(--ink-2)]">Only on notes marked Ready to send. The employee confirms the email in Replai and it goes out in the Gorgias thread as them, signed with their name. Nothing is sent if the customer or a colleague wrote since the note.</span>
            </span>
          </label>
          <label className="mt-4 flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={c.auto_priority_enabled} disabled={busy}
              onChange={(e) => save({ auto_priority_enabled: e.target.checked })} />
            <span>
              <span className="font-medium">Set ticket priority when the Bible asks for it</span>
              <span className="block text-sm text-[var(--ink-2)]">For example hold or postpone requests become high priority. Replai never lowers a priority.</span>
            </span>
          </label>
          <label className="mt-4 flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={c.placeholder_customer_sweep_enabled} disabled={busy}
              onChange={(e) => save({ placeholder_customer_sweep_enabled: e.target.checked }, e.target.checked
                ? 'Move open tickets from "LAMPTWIST CUSTOMER" to the customer who replies in them?' : null)} />
            <span>
              <span className="font-medium">Fix tickets filed under "LAMPTWIST CUSTOMER"</span>
              <span className="block text-sm text-[var(--ink-2)]">When a customer replies to one of our automatic emails, Gorgias keeps the ticket under LAMPTWIST CUSTOMER. Replai moves it to the profile of the address the customer replies from and leaves an internal note. Checks every few minutes, in all views.</span>
            </span>
          </label>
        </section>

        <section className="mt-8 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
          <h2 className="text-[1.1rem] font-semibold">Scheduled replies</h2>
          <label className="mt-4 flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={!!c.scheduled_replies_enabled} disabled={busy}
              onChange={(e) => save({ scheduled_replies_enabled: e.target.checked },
                e.target.checked ? 'Turn on scheduled replies? Replies Replai is sure about (order status, product questions, pre-sales) will go out to customers at the daily time unless the team blocks them.' : null)} />
            <span>
              <span className="font-medium">Send the replies Replai is sure about at set times, unless blocked</span>
              <span className="block text-sm text-[var(--ink-2)]">Order status, product questions and pre-sales only; never complaints, cancellations or money. The team reviews them in Scheduled replies. Every check of the Send button runs again at send time.</span>
            </span>
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <p className="w-full text-[var(--ink-2)]">Replies go out at natural times, Monday to Saturday: usually within 30 minutes of the draft (5 minutes to 1 hour), 08:30 to 18:00, overnight ones spread over the morning. The times below only apply with scheduled_replies_natural off.</p>
            <label className="flex items-center gap-2">Send at (Monday to Saturday)
              <input type="text" defaultValue={c.scheduled_replies_time || '09:30, 11:30, 13:30, 16:30'} disabled={busy}
                onBlur={(e) => e.target.value && e.target.value !== c.scheduled_replies_time && save({ scheduled_replies_time: e.target.value })}
                placeholder="09:30, 11:30, 13:30, 16:30"
                className="w-56 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            </label>
            <label className="flex items-center gap-2" title="Replies Replai is very sure about (confidence 0.90+ and a 5/5 fact-check) also go out at this earlier time">Very sure ones also at
              <input type="text" defaultValue={c.scheduled_replies_early_time || '08:30'} disabled={busy}
                onBlur={(e) => e.target.value && e.target.value !== c.scheduled_replies_early_time && save({ scheduled_replies_early_time: e.target.value })}
                placeholder="08:30"
                className="w-20 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            </label>
            <label className="flex items-center gap-2">Team account (Gorgias email) they are sent from
              <input type="email" defaultValue={c.scheduled_replies_sender || ''} disabled={busy} placeholder="support@lamptwist.be"
                onBlur={(e) => e.target.value !== (c.scheduled_replies_sender || '') && save({ scheduled_replies_sender: e.target.value })}
                className="w-64 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            </label>
          </div>
          <label className="mt-4 block text-sm">Signature under every scheduled reply
            <textarea rows={2} defaultValue={c.scheduled_replies_signature ?? 'Emma\nLampTwist Customer Service'} disabled={busy}
              onBlur={(e) => e.target.value !== c.scheduled_replies_signature && save({ scheduled_replies_signature: e.target.value })}
              className="mt-1 block w-80 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            <span className="mt-1 block text-[var(--ink-3)]">Also for replies a colleague approved or edited: the customer always sees this name. Who approved is kept in Replai.</span>
          </label>
        </section>

        <section className="mt-8 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
          <h2 className="text-[1.1rem] font-semibold">Julia (Buddy)</h2>
          <p className="mt-1 text-sm text-[var(--ink-2)]">The same scheduled replies, on Buddy tickets, signed Julia. Per mailbox: Gorgias (Lia answers, Julia reads nothing), shadow (Julia posts an internal note in Buddy with what she would send, and is compared with Lia in Scheduled replies → Julia (ombre)), or live (Julia answers in Buddy, Lia holds that mailbox). Nothing is written in Buddy until its Julia key is set.</p>
          <JuliaModes value={c.julia_mode} busy={busy} save={save} />
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">Signature
              <input type="text" defaultValue={c.julia_signature || 'Julia'} disabled={busy}
                onBlur={(e) => e.target.value.trim() && e.target.value !== c.julia_signature && save({ julia_signature: e.target.value })}
                className="w-48 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            </label>
            <label className="flex items-center gap-2" title="Bounds the model cost of the shadow">Shadow drafts per day at most
              <input type="number" min="0" defaultValue={c.julia_shadow_daily_max ?? 150} disabled={busy}
                onBlur={(e) => e.target.value !== String(c.julia_shadow_daily_max) && save({ julia_shadow_daily_max: Number(e.target.value) })}
                className="w-24 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1" />
            </label>
          </div>
        </section>

        <section className="mt-8 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
          <h2 className="text-[1.1rem] font-semibold">Drafting, notes and assignment</h2>
          {[
            ['auto_dispatch_enabled', 'Draft a reply for every new ticket',
              'Replai reads each new ticket in the views and its follow-ups, and drafts a reply.',
              'Stop drafting new tickets? Replai will no longer read or draft anything until this is turned back on.'],
            ['auto_note_enabled', 'Post the draft as an internal note in Gorgias',
              'The draft appears in the ticket for the agent, with 👍/👎 and the send button. Never sent to the customer by itself.',
              'Stop posting notes in Gorgias? Drafts will only be visible in Replai.'],
            ['auto_assign_enabled', 'Assign new tickets to the agent who handled this customer before',
              'Only tickets without an assignee; carrier, supplier and automatic mails stay unassigned.', null],
          ].map(([key, title, help, confirmOff]) => (
            <label key={key} className="mt-4 flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={!!c[key]} disabled={busy}
                onChange={(e) => save({ [key]: e.target.checked }, !e.target.checked ? confirmOff : null)} />
              <span>
                <span className="font-medium">{title}</span>
                <span className="block text-sm text-[var(--ink-2)]">{help}</span>
              </span>
            </label>
          ))}
        </section>
      </div>
    </V2Shell>
  );
};

export default ControlV2;
